import React, { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import { useOrganizationStore } from '../../store/organization.store';
import { useCollectionCache } from '../../services/collectionCache.service';

// Keep in sync with the dropdown options used elsewhere (ChaseListUploader,
// AddAudioForm). Same shape so values are interchangeable.
const LANGUAGE_OPTIONS = [
  { label: 'English',    value: 'english' },
  { label: 'Afrikaans',  value: 'afrikaans' },
  { label: 'Zulu',       value: 'zulu' },
  { label: 'Xhosa',      value: 'xhosa' },
  { label: 'Sotho',      value: 'sotho' },
  { label: 'Tswana',     value: 'tswana' },
  { label: 'Pedi',       value: 'pedi' },
  { label: 'Venda',      value: 'venda' },
  { label: 'Tsonga',     value: 'tsonga' },
  { label: 'Swazi',      value: 'swazi' },
  { label: 'Ndebele',    value: 'ndebele' },
  { label: 'Portuguese', value: 'portuguese' },
  { label: 'French',     value: 'french' },
  { label: 'Other',      value: 'other' },
];

const useStyles = makeStyles({
  autocomplete: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      color: '#fff',
      fontSize: '0.85rem',
      padding: '4px 6px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: '0.85rem' },
    '& .MuiChip-root': {
      height: '22px',
      fontSize: '0.75rem',
      backgroundColor: 'var(--accent-1-color)',
      color: '#fff',
    },
    '& .MuiChip-deleteIcon': { color: '#fff', fontSize: '16px' },
  },
});

interface Props {
  firebase: any;
}

const AcceptedLanguagesEditor: React.FC<Props> = ({ firebase }) => {
  const classes = useStyles();
  const currentOrgId = useOrganizationStore((s) => s.currentOrgId);
  // Resolve from the global broadcaster cache instead of a Firestore query.
  // The cache holds every /broadcaster entry; we just find the one whose
  // linkedOrgId matches the current org. Reactive so a GOM edit elsewhere
  // refreshes this view via refreshGlobalEntries.
  const cachedBroadcasters = useCollectionCache((s) => s.broadcaster);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ label: string; value: string }[]>([]);
  const [initial, setInitial] = useState<string[]>([]);

  // Find the broadcaster doc backing the current org. May be null if this
  // org isn't in the global broadcaster list at all.
  const broadcasterEntry = currentOrgId
    ? cachedBroadcasters.find((b: any) => b.linkedOrgId === currentOrgId) || null
    : null;
  const broadcasterDocId = broadcasterEntry ? (broadcasterEntry as any).id : null;
  // Loading state is now driven by whether the cache itself has loaded yet.
  const loading = cachedBroadcasters.length === 0 && !!currentOrgId && !!firebase;

  // Hydrate local edit state from the cached broadcaster entry whenever it
  // changes (org switch, refreshGlobalEntries called).
  useEffect(() => {
    const values: string[] = broadcasterEntry && Array.isArray((broadcasterEntry as any).acceptedLanguages)
      ? (broadcasterEntry as any).acceptedLanguages
      : [];
    setInitial(values);
    setSelected(
      values
        .map((v) => LANGUAGE_OPTIONS.find((lo) => lo.value === v))
        .filter(Boolean) as { label: string; value: string }[],
    );
  }, [broadcasterEntry]);

  const isDirty = (() => {
    const sel = selected.map((o) => o.value).sort();
    const init = [...initial].sort();
    if (sel.length !== init.length) return true;
    return sel.some((v, i) => v !== init[i]);
  })();

  const handleSave = async () => {
    if (!firebase || !broadcasterDocId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const values = selected.map((o) => o.value);
      await updateDoc(doc(firebase.db, 'broadcaster', broadcasterDocId), {
        acceptedLanguages: values,
      });
      setInitial(values);
      // Refresh the global cache so chase-list parser and JobDetail see the
      // new language whitelist without a hard refresh.
      await useCollectionCache.getState().refreshGlobalEntries(firebase);
      setSuccess('Accepted languages saved.');
    } catch (err: any) {
      setError(err?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>
        <Spinner size="sm" /> <span style={{ fontSize: '0.85rem' }}>Loading…</span>
      </div>
    );
  }

  if (!broadcasterDocId) {
    return (
      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', margin: 0 }}>
        This organisation is not registered as a broadcaster in the global list, so language
        restrictions can't be applied.
      </p>
    );
  }

  return (
    <div>
      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
        Choose which spot languages this station accepts. When empty, all languages are accepted.
        Studios will see a warning at delivery time if they try to send a spot in a language not
        on this list (with an option to override).
      </p>

      <Autocomplete
        multiple
        size="small"
        options={LANGUAGE_OPTIONS}
        getOptionLabel={(o) => o.label}
        getOptionSelected={(o, v) => o.value === v.value}
        value={selected}
        onChange={(_, value) => setSelected(value as { label: string; value: string }[])}
        renderTags={(value, getTagProps) =>
          value.map((o, i) => (
            <Chip key={o.value} label={o.label} {...getTagProps({ index: i })} size="small" />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} variant="outlined" className={classes.autocomplete} placeholder="Accept all languages" />
        )}
        className={classes.autocomplete}
      />

      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-sm btn-primary-custom btn-round"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          <span>
            {saving ? (
              <><Spinner size="sm" className="me-2" />Saving…</>
            ) : 'Save accepted languages'}
          </span>
        </button>
        {!isDirty && !saving && (
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>No changes</span>
        )}
      </div>

      {error && (
        <div style={{ color: 'var(--button-danger)', marginTop: '0.5rem', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="text-success" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
          {success}
        </div>
      )}
    </div>
  );
};

export default AcceptedLanguagesEditor;
